import React, { useRef } from "react";
import { type BottomSheetRef, BottomSheet } from "react-spring-bottom-sheet";
import {
  BOTTOM_SHEET_MAX_HEIGHT,
  BOTTOM_SHEET_HALF_HEIGHT,
  BOTTOM_SHEET_MIN_HEIGHT,
} from "../../../consts";
import { Box } from "@wix/design-system";
import { SURFACE_SUNKEN_COLOR } from "@wix/picasso-common";
import "./mobileBottomSheet.css";
import "react-spring-bottom-sheet/dist/style.css";

export const MobileBottomSheet = ({
  open,
  onClose,
  defaultSnap,
  shouldBlockView,
  customSnapPoints,
  isMainChatElement,
  fitContent = false,
  children,
  sibling,
  refFromParent,
}: {
  open: boolean;
  onClose?: () => void;
  isMainChatElement?: boolean;
  defaultSnap?: ({
    maxHeight,
    minHeight,
    height,
  }: {
    maxHeight: number;
    minHeight: number;
    height?: number;
  }) => number;
  shouldBlockView?: boolean;
  customSnapPoints?: number[];
  fitContent?: boolean;
  children: React.ReactNode;
  sibling?: React.ReactNode;
  refFromParent?: React.RefObject<BottomSheetRef>;
}) => {
  const sheetRef = useRef<BottomSheetRef>(undefined!);
  const focusRef = useRef<HTMLButtonElement>(undefined!);

  const getSnapPoints = ({
    maxHeight,
    minHeight,
  }: {
    maxHeight: number;
    minHeight: number;
  }) => {
    if (customSnapPoints) {
      return customSnapPoints.map((point) => maxHeight * point);
    }
    if (fitContent) {
      return [minHeight]; // Only one snap point that fits content
    }

    return [
      maxHeight * BOTTOM_SHEET_MAX_HEIGHT,
      maxHeight * BOTTOM_SHEET_HALF_HEIGHT,
      maxHeight * BOTTOM_SHEET_MIN_HEIGHT,
    ];
  };

  const getDefaultSnap = ({
    maxHeight,
    minHeight,
    height,
  }: {
    maxHeight: number;
    minHeight: number;
    height?: number;
  }) => {
    if (defaultSnap) {
      return defaultSnap({ maxHeight, minHeight, height });
    }
    return minHeight;
  };

  return (
    <BottomSheet
      open={open}
      onDismiss={onClose}
      data-body-scroll-lock-ignore="true"
      ref={refFromParent || (sheetRef as React.RefObject<BottomSheetRef>)}
      initialFocusRef={focusRef}
      blocking={shouldBlockView}
      defaultSnap={getDefaultSnap}
      sibling={
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 4, // above the overlay (which is at z‑index:3)
          }}
        >
          {sibling}
        </div>
      }
      snapPoints={getSnapPoints}
    >
      <Box direction="vertical" backgroundColor={SURFACE_SUNKEN_COLOR}>
        {children}
      </Box>
    </BottomSheet>
  );
};
